import { FC, useState, useEffect } from 'react';
import './ZoneLabelExplaTooltipContent.scss';
import { CircadianPhase, EnergyFeedbackType } from '@demind-inc/core';
import { circadianTooltipInfo, EnergyFeedback } from '../../../../Energy';
import { useRecoilState, useRecoilValue } from 'recoil';
import { selectedDateAtom, viewSwitcherAtom } from '../../../../../data-access';
import { Button, Stack, Typography } from '@mui/material';
import { useUpdatePhaseEmaScore } from '../../../../../hooks';
import { HelpOutline } from '@mui/icons-material';
import { CustomTooltip, CustomTooltipContent } from '../../../../common';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Path } from '../../../../../routing';

interface ZoneLabelExplaTooltipContentProps {
  title: string;
  time: string;
  color: string;
  explanation: string;
  phase: CircadianPhase;
  phaseInfoImage: string;
}

const ZoneLabelExplaTooltipContent: FC<ZoneLabelExplaTooltipContentProps> = ({
  title,
  time,
  color,
  explanation,
  phase,
  phaseInfoImage,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedFeedback, setSelectedFeedback] = useState<EnergyFeedbackType>();
  const { updatePhaseEmaScore, getSelectedZoneEma, isFuturePhase } = useUpdatePhaseEmaScore();
  const selectedDate = useRecoilValue(selectedDateAtom);
  const [_, setCurrentView] = useRecoilState(viewSwitcherAtom);

  const zoneEma = getSelectedZoneEma(phase);
  const futurePhase = isFuturePhase(phase);

  useEffect(() => {
    setSelectedFeedback(zoneEma?.energy);
  }, [zoneEma]);

  const handleFeedbackSelect = async (feedbackType: EnergyFeedbackType) => {
    setSelectedFeedback(feedbackType);
    await updatePhaseEmaScore({
      selectedZone: phase,
      feedbackType,
      endTime: time.split(' - ')[1], // to get the end time from the time range
      date: selectedDate.format('YYYY-MM-DD'),
    });
  };

  return (
    <div className="zone-label-tooltip-content">
      <div className="zone-label-tooltip-content__title-group">
        <div className="zone-label-tooltip-content__title--container">
          <div className="zone-label-tooltip-content__title">{title}</div>
          <CustomTooltip title={<CustomTooltipContent {...circadianTooltipInfo} />}>
            <HelpOutline className="zone-label-tooltip-content__title__icon" />
          </CustomTooltip>
        </div>
        <span className="zone-label-tooltip-content__time" style={{ color, borderColor: color }}>
          {time}
        </span>
      </div>
      <div className="zone-label-tooltip-content__expla">
        <Typography
          variant="subtitle2"
          className="zone-label-tooltip-content__explanation"
          component={'p'}
        >
          {explanation}
        </Typography>
        <img
          src={phaseInfoImage}
          alt={t('calendar.energyPhase.phaseInfoImageAlt')}
          className="zone-label-tooltip-content__image"
        />
        {!futurePhase && (
          <Stack gap={0.5} mt={1}>
            <Typography variant="caption" component={'p'}>
              {t('calendar.energyPhase.howEnergized')}
            </Typography>
            <EnergyFeedback
              selectedFeedback={selectedFeedback}
              onFeedbackSelect={handleFeedbackSelect}
              disabled={false}
              width={250}
            />
          </Stack>
        )}
        <Button
          variant="contained"
          className="zone-label-tooltip-content__button"
          onClick={() => {
            setCurrentView('Data');
            navigate(Path.DATA);
          }}
        >
          {t('calendar.energyPhase.viewMoreData')}
        </Button>
      </div>
    </div>
  );
};

export default ZoneLabelExplaTooltipContent;
